import { ReactElement, useCallback, useState, useEffect } from 'react';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import TextBox from '../../../../../../components/Form/TextBox/TextBox';
import FieldWrapper from 'components/Form/FieldWrapper';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { Headline6 } from '../../../../../../components/UI/MDC/Typography/Typography';
import { FORM_KEYS, FORM_INITIAL_VALUES, COUNTRY_DEFAULT } from './_constants';
import { getCountries, getStates } from './_api.js';

/**
 * Collection of input fields making up a home address form
 * @param {*} properties needed by `homeAddress` including but not limited to handleChange()
 * @returns {ReactElement} Home Address Form Group
 */
const HomeAddress = ({ sx, handleChange, labelPosition, required = true }) => {
	const [countryOptions, setCountryOptions] = useState([]);
	const [sCountry, setCountry] = useState(COUNTRY_DEFAULT);
	const [stateOptions, setStateOptions] = useState([]);

	useEffect(() => {
		getCountries().then(payload =>
			setCountryOptions(
				required ? payload : [{ Key: -1, Value: '' }, ...payload]
			)
		);
		getStates(COUNTRY_DEFAULT).then(payload =>
			required ? payload : setStateOptions(['', ...payload])
		);
	}, [required]);

	const isInternational = useCallback(
		country => !(country === '' || country === 'US'),
		[]
	);

	return countryOptions.length > 0 ? (
		<>
			<Headline6>Home Address</Headline6>
			<Field name={FORM_KEYS.ADDRESS_HOME_COUNTRY}>
				{({ field, meta }) => (
					<FieldWrapper
						name={FORM_KEYS.ADDRESS_HOME_COUNTRY}
						label={'Select Country'}
						meta={meta}
						additionalProps={{
							labelPosition: labelPosition,
						}}
						hideErrorMessage
					>
						<FormControl
							sx={sx}
							error={meta.touched && meta.error}
							required={required}
							variant='filled'
						>
							<InputLabel
								id={FORM_KEYS.ADDRESS_HOME_COUNTRY + '__label'}
							>
								Country
							</InputLabel>
							<Select
								id={FORM_KEYS.ADDRESS_HOME_COUNTRY}
								error={meta.touched && meta.error}
								fullWidth
								label='Country'
								labelId={
									FORM_KEYS.ADDRESS_HOME_COUNTRY + '__label'
								}
								name={FORM_KEYS.ADDRESS_HOME_COUNTRY}
								onBlur={field.onBlur}
								onChange={async e => {
									field.onChange(e);
									const value =
										e && e.target && e.target.value
											? e.target.value
											: '';

									// reset state / province value when input type changes
									if (sCountry !== value) {
										handleChange({
											target: {
												name: FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE,
												value: FORM_INITIAL_VALUES[
													FORM_KEYS
														.ADDRESS_HOME_STATE_PROVINCE
												],
											},
										});
									}
									setCountry(value);
									const response = await getStates(value);
									setStateOptions(
										required ? response : ['', ...response]
									);
								}}
								value={field.value}
								variant='filled'
							>
								{countryOptions.map(country => (
									<MenuItem
										sx={{
											color: country.Value
												? 'inherit'
												: 'transparent',
										}}
										key={country.Key}
										value={country.Value}
									>
										{country.Value ? country.Value : '--'}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{meta.touched && meta.error ? meta.error : ' '}
							</FormHelperText>
						</FormControl>
					</FieldWrapper>
				)}
			</Field>
			<TextBox
				label='Street Address Line 1'
				placeholder='Type address line 1'
				name={FORM_KEYS.ADDRESS_HOME_STREET_1}
				type='text'
				labelPosition={labelPosition}
			/>
			<TextBox
				label='Street Address Line 2'
				placeholder='Type address line 2'
				name={FORM_KEYS.ADDRESS_HOME_STREET_2}
				type='text'
				labelPosition={labelPosition}
			/>
			<TextBox
				label='City'
				placeholder='Type a city'
				name={FORM_KEYS.ADDRESS_HOME_CITY}
				type='text'
				labelPosition={labelPosition}
			/>
			<Field name={FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE}>
				{({ field, meta }) => (
					<FieldWrapper
						name={FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE}
						label={
							'Select ' +
							(isInternational(sCountry) ? 'Province' : 'State')
						}
						meta={meta}
						additionalProps={{
							labelPosition: labelPosition,
						}}
						hideErrorMessage
					>
						<FormControl
							sx={sx}
							error={meta.touched && meta.error}
							required={required}
							variant='filled'
						>
							<InputLabel
								id={
									FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE +
									'__label'
								}
							>
								{isInternational(sCountry)
									? 'Province'
									: 'State'}
							</InputLabel>
							<Select
								id={FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE}
								error={meta.touched && meta.error}
								fullWidth
								label={
									isInternational(sCountry)
										? 'Province'
										: 'State'
								}
								labelId={
									FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE +
									'__label'
								}
								name={FORM_KEYS.ADDRESS_HOME_STATE_PROVINCE}
								onBlur={field.onBlur}
								onChange={field.onChange}
								value={field.value}
								variant='filled'
							>
								{stateOptions.map((state, index) => (
									<MenuItem
										sx={{
											color: state
												? 'inherit'
												: 'transparent',
										}}
										key={index}
										value={state}
									>
										{state ? state : '--'}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{meta.touched && meta.error ? meta.error : ' '}
							</FormHelperText>
						</FormControl>
					</FieldWrapper>
				)}
			</Field>
			<TextBox
				label={isInternational(sCountry) ? 'Postal Code' : 'Zip Code'}
				name={FORM_KEYS.ADDRESS_HOME_ZIP_POSTAL}
				type='text'
				labelPosition={labelPosition}
				placeholder={
					'Type a ' + isInternational(sCountry)
						? 'Postal Code'
						: 'Zip Code'
				}
				validate={value => {
					let errorMessage;
					if (required && !isInternational(sCountry)) {
						const zipRegex = new RegExp(/^[0-9]{5}$/);
						if (!zipRegex.test(value)) {
							errorMessage = 'Invalid Zip Code Format';
						}
					}
					return errorMessage;
				}}
			/>
		</>
	) : null;
};

HomeAddress.propTypes = {
	handleChange: PropTypes.func.isRequired,
	required: PropTypes.bool,
};

export { HomeAddress };
